import { z } from 'zod';

// Define the schema for a line item in the sales order
export const salesOrderItemSchema = z.object({
  id: z.number().optional(), // Only present when editing an existing item
  item_id: z.number({ required_error: 'Product is required' }),
  quantity: z.number().min(0.01, { message: 'Quantity must be greater than 0' }),
  rate: z.number().min(0, { message: 'Rate must be 0 or greater' }),
  discount_percent: z.number().min(0).max(100).default(0),
  discount_amount: z.number().min(0).default(0), // Add discount_amount field
  tax_rate_id: z.number().nullable().optional(), // Add tax_rate_id field
  amount: z.number(),
  description: z.string().optional().nullable(),
  godown_id: z.number({ required_error: 'Warehouse is required' }),
  applied_sale_rule_id: z.number().nullable().optional(), // Add applied_sale_rule_id field
  unit_id: z.number().optional(), // Add unit_id field for API
  // Additional display fields
  item_name: z.string().optional(),
  item_code: z.string().optional(),
  taxable_amount: z.number().optional(),
  tax_amount: z.number().optional(),
  // Fields for calculations
  is_tax_inclusive: z.boolean().optional(),
  tax_rate_percent: z.number().optional(),
  rate_decimal_places: z.number().optional(),
}).refine((data) => {
  // Ensure only one type of discount is provided
  if (data.discount_percent > 0 && data.discount_amount > 0) {
    return false;
  }
  return true;
}, {
  message: "Provide either discount percentage or discount amount, not both",
  path: ["discount_percent"], // Show error on discount_percent field
});

// Define the schema for the entire sales order form
export const salesOrderSchema = z.object({
  party_id: z.number({ required_error: 'Customer is required' }),
  site_id: z.number({ required_error: 'Site is required' }),
  transaction_date: z.date({ required_error: 'Date is required' }),
  expected_delivery_date: z.date().optional().nullable(),
  reference_number: z.string().optional().nullable(),
  narration: z.string().optional().nullable(),
  total_amount: z.number().min(0),
  items: z.array(salesOrderItemSchema)
    .min(1, { message: 'At least one product is required' }),
});

// Create a type for form values based on the schema
export type SalesOrderFormValues = z.infer<typeof salesOrderSchema>;

// Create a type for line items
export type SalesOrderItemFormValues = z.infer<typeof salesOrderItemSchema>;

// Keep the old type name for backward compatibility
export type DocumentItemFormValues = SalesOrderItemFormValues;